import styles from '@components/components/Slider/GallerySlider/GallerySlider.module.scss';
import Lightbox from '@components/components/Lightbox/Lightbox';
import {
  determinedAmountOfLoopedSlides,
  multiplySlidesIfNecessary,
} from '@components/components/Slider/GallerySlider/lib/loopMode';
import GalleryImage from '@components/components/Slider/GallerySlider/components/GalleryImage/GalleryImage';
import { useMedia } from '@components/hooks';
import PropTypes from 'prop-types';
import React, { useMemo, useState, Fragment, useEffect } from 'react';
import classNames from 'classnames';
import {
  usePreventScroll,
  Slider,
  SliderControl,
} from '@wienenergiegithub/ui-next';

const GallerySlider = ({
  headingLevel = 3,
  images: gallerySlides,
  isExtended = false,
  hasLightbox = true,
}) => {
  const [open, setOpen] = useState(false);
  const [sliderController, setSliderController] = useState(false);
  const [lightBoxController, setLightBoxController] = useState(false);
  const [slidesToDisplay, loopedSlides] = useMemo(() => {
    const duplicatedSlides = multiplySlidesIfNecessary(gallerySlides);
    const amountOfLoopedSlides =
      determinedAmountOfLoopedSlides(duplicatedSlides);
    return [duplicatedSlides, amountOfLoopedSlides];
  }, [gallerySlides]);

  const defaultSettings = {
    slidesPerView: 1,
    centeredSlides: true,
    loop: true,
    loopedSlides,
    speed: 800,
  };

  const sliderSettings = {
    ...defaultSettings,
    // prevent double triggering of keyboard event (slider and lightbox)
    keyboard: {
      enabled: !open,
    },
    // synchronization of active slide
    controller: { control: lightBoxController },
  };

  const lightboxSettings = {
    ...defaultSettings,
    // prevent double triggering of keyboard event (slider and lightbox)
    keyboard: {
      enabled: open,
    },
    // synchronization of active slide
    controller: { control: sliderController },
  };

  const isMobile = useMedia('(max-width: 576px)');

  const handleLightboxToggle = () => {
    if (!isMobile && hasLightbox) {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (isMobile && open) {
      setOpen(false);
    }
  }, [isMobile, open]);

  usePreventScroll(open && !isMobile);

  return (
    <>
      <Lightbox
        headingLevel={headingLevel}
        images={slidesToDisplay}
        isExtended={isExtended}
        lightboxSettings={lightboxSettings}
        onSwiper={setLightBoxController}
        open={open}
        setOpen={setOpen}
      />

      <Slider
        {...sliderSettings}
        className={styles.slider}
        onSwiper={setSliderController}
      >
        <SliderControl
          className={classNames(styles.arrow, styles.leftArrow, {
            [styles.extended]: isExtended,
          })}
          data-no-slide="true"
          direction="left"
        />
        <SliderControl
          className={classNames(styles.arrow, styles.rightArrow, {
            [styles.extended]: isExtended,
          })}
          data-no-slide="true"
          direction="right"
        />
        {slidesToDisplay?.map(gallerySlide => (
          <Fragment key={gallerySlide?.id}>
            <GalleryImage
              hasLightbox={hasLightbox}
              headingLevel={headingLevel}
              isExtended={isExtended}
              {...gallerySlide}
              onClick={handleLightboxToggle}
            />
          </Fragment>
        ))}
      </Slider>
    </>
  );
};

GallerySlider.propTypes = {
  hasLightbox: PropTypes.bool,
  headingLevel: PropTypes.number,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      alt: PropTypes.string.isRequired,
      caption: PropTypes.string,
      content: PropTypes.string,
      header: PropTypes.string,
      link: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        target: PropTypes.string,
        variant: PropTypes.string,
      }),
      src: PropTypes.string.isRequired,
      srcSet: PropTypes.string,
    }),
  ).isRequired,
  isExtended: PropTypes.bool,
};

export default GallerySlider;
